import mapiConfig from "../configs/mapi";
import { Attachment } from "../models/sampleRequestModel";
import { httpMStepsClient } from "./utils/httpService";

const MAPI_URL = mapiConfig.api.domain;

export interface SampleRequestListParams {
  group?: string;
  status?: string | null;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const getSampleRequests = async (params: SampleRequestListParams) => {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  else if (params.group && params.group !== "All")
    query.set("group", params.group);
  if (params.search) query.set("search", params.search);
  if (params.page) query.set("page", String(params.page));
  if (params.pageSize) query.set("pageSize", String(params.pageSize));

  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: `/sample-requests?${query.toString()}`,
  });
};

export const getSampleRequestStatusCounts = async () => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: "/sample-requests/status-counts",
  });
};

export const getSampleRequestById = async (id: string) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "GET",
    path: `/sample-requests/${id}`,
  });
};

export const createSampleRequest = async (payload: {
  requestedBy: string;
  sampleInfo: Record<string, any>;
  deliveryInfo: Record<string, any>;
}) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "POST",
    path: "/sample-requests",
    body: payload,
  });
};

export const updateSampleRequest = async (
  id: string,
  payload: {
    actorId: string;
    actorName: string;
    sampleInfo?: Record<string, any>;
    deliveryInfo?: Record<string, any>;
  },
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}`,
    body: payload,
  });
};

export const approveSampleRequest = async (
  id: string,
  actorId: string,
  actorName: string,
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/approve`,
    body: { actorId, actorName },
  });
};

export const rejectSampleRequest = async (
  id: string,
  actorId: string,
  actorName: string,
  reason: string,
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/reject`,
    body: { actorId, actorName, reason },
  });
};

export const requestSampleRequestChanges = async (
  id: string,
  actorId: string,
  actorName: string,
  remarks: string,
  requestedFor: "Sales" | "China",
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/request-changes`,
    body: { actorId, actorName, remarks, requestedFor },
  });
};

export const submitSampleRequestChanges = async (
  id: string,
  actorId: string,
  actorName: string,
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/submit-changes`,
    body: { actorId, actorName },
  });
};

export const approveSampleRequestCAD = async (
  id: string,
  actorId: string,
  actorName: string,
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/approve-cad`,
    body: { actorId, actorName },
  });
};

export const submitSampleRequestProduction = async (
  id: string,
  actorId: string,
  actorName: string,
  tracking: {
    courier: string;
    trackingNumber: string;
    shippedDate: string;
    attachments?: Attachment[];
  },
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/submit-production`,
    body: { actorId, actorName, ...tracking },
  });
};
export const confirmSampleRequestReceived = async (
  id: string,
  actorId: string,
  actorName: string,
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/confirm-received`,
    body: { actorId, actorName },
  });
};

export const cancelSampleRequest = async (
  id: string,
  actorId: string,
  actorName: string,
  reason?: string,
) => {
  return await httpMStepsClient({
    baseURL: MAPI_URL,
    type: "PATCH",
    path: `/sample-requests/${id}/cancel`,
    body: { actorId, actorName, reason },
  });
};

export const submitSampleRequestToFactory = (
  id: string,
  body: {
    actorId: string;
    actorName: string;
    toolingCost: string;
    toolingRequestBy?: string;
    attachments?: { fileName: string; fileUrl: string; uploadedBy?: string }[];
  },
) =>
  httpMStepsClient({
    baseURL: MAPI_URL, // or whatever base this service uses
    type: "PATCH",
    path: `/sample-requests/${id}/submit-to-factory`,
    body,
  });
